// src/components/Doctor/DoctorProfile.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import api from "../../api";
import "./DoctorProfile.css";

type Advisor = {
  id: string;
  doctor_id?: string | number | null;
  advisor_id?: string | number | null;
  name?: string | null;
  email?: string | null;
  specialties?: string | null;
  specialty?: string | null;
  medical_field?: string | null;
  country?: string | null;
  state?: string | null;
  location?: string | null;
  medical_license_number?: string | null;
  license?: string | null;
  website?: string | null;
  status?: string | null;
  retired?: boolean | number | null;
  is_active?: boolean | number | null;
  is_approved?: boolean | number | null;
  approved?: boolean | number | null;
  bio?: string | null;
  photo_url?: string | null;
  photo?: string | null;
};

type LocationState = {
  doctor?: Partial<Advisor> | null;
  advisor?: Partial<Advisor> | null;
};

const PLACEHOLDER_LOCAL = `${process.env.PUBLIC_URL}/logo192.png`;

function clean(v: any) {
  const s = String(v ?? "").trim();
  return s.toLowerCase() === "string" ? "" : s;
}

function normalizeWebsite(raw?: string | null): string {
  const value = clean(raw);
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  return `https://${value}`;
}

function asBool(v: any, fallback = false): boolean {
  if (v === true || v === 1) return true;
  if (v === false || v === 0) return false;
  const s = clean(v).toLowerCase();
  if (["true", "1", "yes", "y", "active", "approved", "online"].includes(s)) return true;
  if (["false", "0", "no", "n", "inactive", "retired", "disabled"].includes(s)) return false;
  return fallback;
}

function normalizeAdvisor(raw: any): Advisor | null {
  const id = clean(raw?.id ?? raw?.doctor_id ?? raw?.advisor_id);
  if (!id) return null;

  return {
    id,
    doctor_id: raw?.doctor_id ?? raw?.id ?? null,
    advisor_id: raw?.advisor_id ?? raw?.id ?? null,
    name: raw?.name ?? null,
    email: raw?.email ?? null,
    specialties: raw?.specialties ?? raw?.specialty ?? raw?.medical_field ?? null,
    specialty: raw?.specialty ?? raw?.specialties ?? raw?.medical_field ?? null,
    medical_field: raw?.medical_field ?? raw?.specialties ?? raw?.specialty ?? null,
    country: raw?.country ?? null,
    state: raw?.state ?? null,
    location: raw?.location ?? null,
    medical_license_number: raw?.medical_license_number ?? raw?.license ?? null,
    license: raw?.license ?? raw?.medical_license_number ?? null,
    website: raw?.website ?? null,
    status: raw?.status ?? null,
    retired: raw?.retired ?? null,
    is_active: raw?.is_active ?? raw?.active ?? null,
    is_approved: raw?.is_approved ?? raw?.approved ?? null,
    approved: raw?.approved ?? raw?.is_approved ?? null,
    bio: raw?.bio ?? null,
    photo_url: raw?.photo_url ?? raw?.photo ?? null,
    photo: raw?.photo ?? null,
  };
}

function unwrapAdvisor(payload: any): Advisor | null {
  return normalizeAdvisor(
    payload?.doctor ||
      payload?.advisor ||
      payload?.item ||
      payload?.data ||
      payload
  );
}

function getDisplayStatus(advisor: Advisor): string {
  if (asBool(advisor.retired, false)) return "Retired";

  const raw = clean(advisor.status).toLowerCase();
  if (raw === "on call" || raw === "on_call") return "On Call";
  if (raw === "retired") return "Retired";
  if (raw === "inactive" || raw === "disabled") return "Inactive";
  if (raw === "active") return "Active";
  if (clean(advisor.status)) return clean(advisor.status);

  return asBool(advisor.is_active, true) ? "Active" : "Inactive";
}

const DoctorProfile: React.FC = () => {
  const { id: routeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const locationState = (location.state || {}) as LocationState;

  const passedAdvisor = useMemo(
    () => normalizeAdvisor(locationState?.advisor || locationState?.doctor),
    [locationState?.advisor, locationState?.doctor]
  );

  const idParam = clean(routeId || passedAdvisor?.id);

  const role = (
    localStorage.getItem("role") ||
    sessionStorage.getItem("role") ||
    ""
  ).toLowerCase();

  const isAdmin = role === "admin";

  const [advisor, setAdvisor] = useState<Advisor | null>(passedAdvisor || null);
  const [loading, setLoading] = useState(!passedAdvisor);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;

    const loadAdvisor = async () => {
      if (!idParam) {
        if (!alive) return;
        setAdvisor(null);
        setErr("Advisor not found.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setErr("");

      try {
        const res = await api.get(`/admin/doctors/${encodeURIComponent(idParam)}`);
        const normalized = unwrapAdvisor(res?.data);

        if (!alive) return;

        if (!normalized) {
          setAdvisor(passedAdvisor || null);
          setErr(passedAdvisor ? "" : "Advisor not found.");
          return;
        }

        setAdvisor(normalized);
      } catch (error: any) {
        if (!alive) return;
        setAdvisor(passedAdvisor || null);
        setErr(
          passedAdvisor
            ? ""
            : error?.response?.data?.detail ||
                error?.response?.data?.message ||
                error?.message ||
                "Error loading advisor profile."
        );
      } finally {
        if (alive) setLoading(false);
      }
    };

    loadAdvisor();

    return () => {
      alive = false;
    };
  }, [idParam, passedAdvisor]);

  const handleBack = () => {
    if (isAdmin) {
      navigate("/dashboard");
      return;
    }
    navigate(-1);
  };

  if (loading && !advisor) {
    return <p className="loading">Loading advisor profile...</p>;
  }

  if (err && !advisor) {
    return <p className="error-msg">{err}</p>;
  }

  if (!advisor) {
    return <p className="error-msg">Advisor not found.</p>;
  }

  const photoSrc = clean(advisor.photo_url || advisor.photo) || PLACEHOLDER_LOCAL;
  const specialty = clean(advisor.specialties || advisor.specialty || advisor.medical_field) || "N/A";
  const license = clean(advisor.medical_license_number) || clean(advisor.license) || "N/A";
  const displayLocation =
    clean(advisor.location) ||
    [advisor.state, advisor.country].map(clean).filter(Boolean).join(", ") ||
    "N/A";

  const websiteHref = normalizeWebsite(advisor.website);
  const displayStatus = getDisplayStatus(advisor);

  return (
    <div className="profile-container">
      <div className="profile-card">
        <img
          src={photoSrc}
          alt={advisor.name || "Advisor"}
          className="profile-photo"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = PLACEHOLDER_LOCAL;
          }}
        />

        <h2 className="profile-name">{advisor.name || "Unnamed Advisor"}</h2>
        {advisor.email ? <p className="profile-email">{advisor.email}</p> : null}

        <div className="profile-info">
          <p>
            <strong>Specialty:</strong> {specialty}
          </p>
          <p>
            <strong>Location:</strong> {displayLocation}
          </p>
          <p>
            <strong>License / Credential #:</strong> {license}
          </p>
          <p>
            <strong>Website:</strong>{" "}
            {websiteHref ? (
              <a href={websiteHref} target="_blank" rel="noopener noreferrer">
                {advisor.website}
              </a>
            ) : (
              "N/A"
            )}
          </p>
          <p>
            <strong>Status:</strong> {displayStatus}
          </p>
        </div>

        <div className="profile-bio">
          <h3>About This Advisor</h3>
          <p>{clean(advisor.bio) || "—"}</p>
        </div>

        <div className="profile-actions">
          <button onClick={handleBack} className="back-btn" type="button">
            ← {isAdmin ? "Back to Advisors" : "Back"}
          </button>

          {isAdmin ? (
            <button
              onClick={() =>
                navigate(`/edit-doctor/${advisor.id}`, {
                  state: { doctor: advisor, advisor },
                })
              }
              className="edit-btn"
              type="button"
            >
              Edit Advisor Profile
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default DoctorProfile;
